import React from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { SpreadsheetRow } from './spreadsheet-row';
import type { SpreadsheetSectionProps } from './types';

/**
 * SpreadsheetSection Component
 *
 * Collapsible section with:
 * - Header with toggle icon
 * - Rows (hidden when collapsed)
 * - Optional total row
 */
export const SpreadsheetSection: React.FC<SpreadsheetSectionProps> = ({
  section,
  periods,
  onCellChange,
  readOnly,
  expanded,
  onToggle,
  focusedCell,
  rowStartIndex,
  onNavigate,
  onAddRow,
  onDeleteRow
}) => {
  const { collapsible = false } = section;

  return (
    <div className="mb-4">
      {/* Section header */}
      <div
        className="flex items-center px-3 py-2 bg-[#334759] text-white font-semibold cursor-pointer"
        onClick={collapsible ? onToggle : undefined}
        data-testid={`section-header-${section.id}`}
      >
        {collapsible && (
          <span className="mr-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
        )}
        <span>{section.label}</span>
      </div>

      {/* Section rows (shown when expanded or not collapsible) */}
      {(expanded || !collapsible) && (
        <div className="mt-1">
          {section.rows.map((row, index) => (
            <SpreadsheetRow
              key={row.id}
              row={row}
              periods={periods}
              onCellChange={onCellChange}
              readOnly={readOnly}
              focusedCell={focusedCell}
              rowIndex={rowStartIndex + index}
              onNavigate={onNavigate}
              onDeleteRow={onDeleteRow}
            />
          ))}

          {/* Add Row button */}
          {onAddRow && !readOnly && (
            <div
              className="flex items-center px-3 py-1.5 cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors border border-dashed border-gray-200"
              onClick={() => onAddRow?.(section.id ?? 'unknown')}
              data-testid={`add-row-${section.id}`}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="text-sm">Add line item</span>
            </div>
          )}

          {/* Total row for section */}
          {section.totalRow && (
            <SpreadsheetRow
              row={section.totalRow}
              periods={periods}
              onCellChange={onCellChange}
              readOnly={readOnly}
              focusedCell={focusedCell}
              rowIndex={rowStartIndex + section.rows.length}
              onNavigate={onNavigate}
              onDeleteRow={onDeleteRow}
            />
          )}
        </div>
      )}
    </div>
  );
};
